import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { cssWhen } from '../../utils/themeUtils';
import classes from './CountdownBar.module.scss';

export type CountdownBarProps = {
  max?: number;
  remaining: number | undefined;
  getLabel: (remaining: number) => string;
  className?: string;
  shouldUseCountdownBarPlaceholder: boolean;
};

export const CountdownBar = ({
  max,
  remaining,
  getLabel,
  className,
  shouldUseCountdownBarPlaceholder,
}: CountdownBarProps) => {
  if (remaining === undefined || !max || max <= 0 || shouldUseCountdownBarPlaceholder) {
    return (
      <div className={cssWhen(classes.container, className)}>
        <div className={classes.neutralBar} />
      </div>
    );
  }

  return (
    <CountdownBarCounting
      max={max}
      remaining={remaining}
      getLabel={getLabel}
      className={className}
    />
  );
};

type CountdownBarCountingProps = {
  max: number;
  remaining: number;
  getLabel: (remaining: number) => string;
  className?: string;
};

const CountdownBarCounting = ({ max, remaining, getLabel, className }: CountdownBarCountingProps) => {
  const prevRemainingRef = useRef<number>(0);
  const progressRef = useRef<HTMLDivElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);
  const [trackWidth, setTrackWidth] = useState<number>(0);

  const clampedRemaining = Math.max(0, Math.min(remaining, max));
  const percentage = max > 0 ? (clampedRemaining / max) * 100 : 0;
  const label = getLabel(clampedRemaining);

  useLayoutEffect(() => {
    if (!trackRef.current) {
      return;
    }
    const observer = new ResizeObserver(entries => {
      for (const entry of entries) {
        setTrackWidth(entry.contentRect.width);
      }
    });
    observer.observe(trackRef.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!progressRef.current) {
      return;
    }

    const isGrowing = prevRemainingRef.current < clampedRemaining;
    if (isGrowing) {
      progressRef.current.style.transition = 'width 0.3s ease';
    } else {
      progressRef.current.style.transition = 'width 1s linear';
    }
    progressRef.current.style.width = `${percentage}%`;
  }, [clampedRemaining, percentage]);

  useEffect(() => {
    prevRemainingRef.current = remaining;
  }, [remaining]);

  return (
    <div className={cssWhen(classes.container, className)}>
      <div
        ref={trackRef}
        className={classes.track}
      >
        <span className={classes.labelOutside}>{label}</span>
        <div
          ref={progressRef}
          className={classes.progress}
        >
          <div
            className={classes.progressContent}
            style={{ width: trackWidth > 0 ? trackWidth : undefined }}
          >
            <span className={classes.labelInside}>{label}</span>
          </div>
        </div>
      </div>
    </div>
  );
};
